import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import Papa from 'papaparse';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Label, Legend } from 'recharts';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import superagent from 'superagent';
import Loading from '../common/Loading';
import ReactTooltip from 'react-tooltip';
import moment from 'moment';

// Helper to combine class names (will be used for simple state-based styling)
const cn = (...classes) => classes.filter(Boolean).join(' ');

const COLORS = ['#0088cc', '#3da9df', '#6ac0f2', '#97d7ff', '#c4eeff'];

const OutcomeIcon = ({ type }) => {
    const iconName = {
        source: 'phone', 
        booked: 'check_circle', 
        notification: 'notifications',
        cancelled: 'cancel', 
        outcome: 'chat_bubble', 
        other: 'help_outline'
    };
    return <span className="material-icons">{iconName[type] || iconName['other']}</span>;
};

const MilestoneIcon = ({ label }) => {
  const l = label.toUpperCase();
  if (l.includes('STARTED')) return <span className="material-icons">play_arrow</span>;
  if (l.includes('ENDED')) return <span className="material-icons">stop</span>;
  if (l.includes('BOOKING_DONE')) return <span className="material-icons">event_available</span>;
  if (l.includes('BOOKING_REQUEST')) return <span className="material-icons">event</span>;
  if (l.includes('CANCEL_DONE')) return <span className="material-icons">event_busy</span>;
  if (l.includes('CANCEL_REQUEST')) return <span className="material-icons">cancel_schedule_send</span>;
  if (l.includes('RESCHEDULE')) return <span className="material-icons">update</span>;
  if (l.includes('CONFIRMATION')) return <span className="material-icons">thumb_up</span>;
  if (l.includes('NOTIFICATION')) return <span className="material-icons">notifications_active</span>;
  if (l.includes('ACTIONABLE')) return <span className="material-icons">notification_important</span>;
  if (l.includes('INSURANCE')) return <span className="material-icons">receipt_long</span>;
  if (l.includes('STAFF')) return <span className="material-icons">person</span>;
  return <span className="material-icons">bolt</span>;
};

const formatTimestamp = (ts) => {
  if (!ts) return '';
  const parts = ts.split('T');
  if (parts.length < 2) return ts;
  const date = parts[0];
  const time = parts[1].split('.')[0].replace(/\s/g, '');
  return `${date} ${time}`;
};

const getNodeType = (outcome) => {
  const o = String(outcome || '').toUpperCase();
  if (o === 'BOOKED') return 'booked';
  if (o === 'NOTIFICATION') return 'notification';
  if (o === 'CANCELLED') return 'cancelled';
  return 'outcome';
};

const parseTranscript = (text) => {
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const entries = [];
  
  let currentEntry = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    const timestampRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\s*\d{2}:\s*\d{2}\.\d+/;
    if (timestampRegex.test(line)) {
      if (currentEntry && currentEntry.timestamp) {
        entries.push(currentEntry);
      }
      currentEntry = { timestamp: line, content: '' };
      continue;
    }

    if (!currentEntry) continue;

    if (line.startsWith('EVENT:') || line.startsWith('MESSAGE:')) {
      const type = line.startsWith('EVENT:') ? 'EVENT' : 'MESSAGE';
      const parts = line.split('--').map(p => p.trim());
      const labelPart = parts[0].split(':')[1]?.trim() || '';
      
      currentEntry.type = type;
      currentEntry.label = labelPart;
      
      const originPart = parts.find(p => p.includes('Origin:'));
      if (originPart) {
        currentEntry.origin = originPart.split(':')[1]?.trim();
      }
      continue;
    }

    // Otherwise, it's content
    // Remove trailing dashes (2 or more) and skip lines that are purely dashes
    if (/^-+$/.test(line)) continue;
    const cleanedLine = line.replace(/-{2,}\s*$/g, '').trim();
    if (cleanedLine) {
      currentEntry.content = (currentEntry.content ? currentEntry.content + '\n' : '') + cleanedLine;
    }
  }

  if (currentEntry && currentEntry.timestamp) {
    entries.push(currentEntry);
  }

  return entries;
};

const getMilestones = (entries, conversationInfo) => {
  if (entries.length === 0) return [];

  const milestones = [];
  const seenMilestones = new Set();

  // Pre-scan to check if this is a booking, cancel, or reschedule conversation
  const isBookingCancelReschedule = entries.some(entry => {
    const content = entry.content.toUpperCase();
    const label = entry.label.toUpperCase();
    return label === 'NEW_BOOKING' || content.includes('BOOKED') || 
           label === 'CANCELLED' || content.includes('CANCELLED') ||
           content.includes('RESCHEDULED') ||
           (label === 'WORKFLOW' && (
             content.includes('NEW_BOOKING') || 
             content.includes('PROPOSE AND BOOK') || 
             content.includes('CANCEL') || 
             content.includes('RESCHEDULE')
           ));
  });

  // Helper to extract a cleaner booking summary
  const summarizeBooking = (content) => {
    const upper = content.toUpperCase();
    if (upper.includes('BOOKED')) {
      // Try to clean up common patterns
      return content
        .replace(/BOOKED/i, 'Appointment Booked:')
        .replace(/CATEGORY:.*|INTENT:.*|ORIGIN:.*/gi, '')
        .trim();
    }
    return content;
  };

  // 1. Call Started & Initiated By
  const firstEntry = entries[0];
  const source = conversationInfo?.['Conversation source'] || 'Unknown Source';
  milestones.push({
    ...firstEntry,
    type: 'EVENT',
    label: 'CONVERSATION_STARTED',
    content: `Conversation initiated via ${source}. Started at ${formatTimestamp(firstEntry.timestamp)}`
  });

  entries.forEach((entry) => {
    const content = entry.content.toUpperCase();
    const label = entry.label.toUpperCase();

    // Helper to add unique milestones
    const addMilestone = (milestoneLabel, entryOverride) => {
      let finalContent = entryOverride?.content || entry.content;
      let notificationIntent = null;
      
      // Hide content for requests as per user request
      if (milestoneLabel.includes('REQUEST')) {
        const action = milestoneLabel.split('_')[0].toLowerCase();
        finalContent = `Patient initiated ${action} request.`;
      }

      if (milestoneLabel === 'BOOKING_DONE') {
        finalContent = summarizeBooking(finalContent);
      }

      if (milestoneLabel.includes('NOTIFICATION')) {
        const intentMatch = finalContent.match(/Intent:\s*([^,]+)/i);
        if (intentMatch) notificationIntent = intentMatch[1].trim();

        finalContent = finalContent
          .replace(/INTENT:.*?CATEGORY:[A-Z]+/gi, '')
          .trim();
      }

      // Clean up content: remove all trailing dashes (2 or more) and extra whitespace
      finalContent = finalContent.replace(/-{2,}\s*$/gm, '').trim();

      // Deduplicate major outcomes to prevent double-showing (e.g. from both EVENT and MESSAGE)
      // We deduplicate based on label and content to allow multiple unique bookings
      const majorOutcomes = ['BOOKING_DONE', 'CANCEL_DONE', 'RESCHEDULE_DONE', 'CONFIRMATION_DONE', 'BOOKING_REQUEST', 'CANCEL_REQUEST', 'RESCHEDULE_REQUEST', 'CONFIRMATION_REQUEST', 'INSURANCE_CAPTURED'];
      const milestoneKey = `${milestoneLabel}:${finalContent}`;
      
      if (majorOutcomes.includes(milestoneLabel) && seenMilestones.has(milestoneKey)) {
        return;
      }

      milestones.push({ 
        ...entry, 
        label: milestoneLabel, 
        ...entryOverride,
        content: finalContent,
        notificationIntent
      });
      
      if (majorOutcomes.includes(milestoneLabel)) {
        seenMilestones.add(milestoneKey);
      }
    };

    // 2. Major Outcomes & Requests
    if (entry.type === 'EVENT') {
      // 3. Notifications (FYI/Actionable) - Catch these first to avoid misidentification
      if (label.includes('NOTIFICATION') || content.includes('CATEGORY:')) {
        if (content.includes('CATEGORY:FYI') || content.includes('CATEGORY:ACTIONABLE')) {
          const isActionable = content.includes('CATEGORY:ACTIONABLE');
          const milestoneLabel = isActionable ? 'ACTIONABLE_NOTIFICATION' : 'NOTIFICATION (FYI)';
          addMilestone(milestoneLabel);
          return;
        }
      }

      // Booking
      if (label === 'NEW_BOOKING' || (label !== 'WORKFLOW' && content.includes('BOOKED') && !content.includes('REQUEST'))) {
        addMilestone('BOOKING_DONE');
      } else if (label === 'WORKFLOW' && (content.includes('NEW_BOOKING') || content.includes('PROPOSE AND BOOK'))) {
        addMilestone('BOOKING_REQUEST');
      }
      // Cancellation
      else if (label === 'CANCELLATION' || label === 'CANCELLED') {
        addMilestone('CANCEL_DONE');
      } else if (label === 'WORKFLOW' && content.includes('CANCEL')) {
        addMilestone('CANCEL_REQUEST');
      }
      // Reschedule
      else if (label === 'RESCHEDULE' || label === 'RESCHEDULED' || (label !== 'WORKFLOW' && content.includes('RESCHEDULED') && !content.includes('REQUEST'))) {
        addMilestone('RESCHEDULE_DONE');
      } else if (label === 'WORKFLOW' && content.includes('RESCHEDULE')) {
        addMilestone('RESCHEDULE_REQUEST');
      }
      // Confirmation
      else if (label === 'CONFIRMATION' || content.includes('CONFIRMATION SUCCESSFUL')) {
        addMilestone('CONFIRMATION_DONE');
      } else if (label === 'WORKFLOW' && (content.includes('CONFIRMATION') || content.includes('REMINDER'))) {
        if (!isBookingCancelReschedule) {
          addMilestone('CONFIRMATION_REQUEST');
        }
      }
      // 4. Insurance
      else if (label === 'INSURANCE_RECEIVED' || content.includes('INSURANCE')) {
        addMilestone('INSURANCE_CAPTURED');
      }
      // 5. Staff Assignment
      else if (label === 'BUSINESS_ACTION_CONVERSATION_ASSIGNMENT_STATUS' || content.includes('ASSIGNED TO')) {
        addMilestone('STAFF_ASSIGNED');
      }
    } 
    else if (entry.type === 'MESSAGE' && content.includes('CATEGORY:')) {
      const category = content.includes('CATEGORY:FYI') ? 'FYI' : 'ACTIONABLE';
      addMilestone(`NOTIFICATION (${category})`);
    }
  });

  // 6. Conversation Ended
  const lastEntry = entries[entries.length - 1];
  let endReason = "Conversation concluded.";
  const hasEscalation = entries.some(e => e.content.toUpperCase().includes('ESCALATED'));
  if (hasEscalation) endReason = "Conversation ended with escalation to staff.";
  
  milestones.push({
    ...lastEntry,
    type: 'EVENT',
    label: 'CONVERSATION_ENDED',
    content: endReason
  });

  return milestones;
};

const CustomYAxisTick = ({ x, y, payload }) => {
    const { value } = payload;
    const truncatedValue = value.length > 40 ? `${value.substring(0, 37)}...` : value;
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={0} textAnchor="end" fill="#666" fontSize={12} data-tip={value} data-for="journey-tooltip">
          {truncatedValue}
        </text>
      </g>
    );
  };

const NewJourneyView = ({ csvData, business, auth }) => {
  const [data, setData] = useState([]);
  const [accountName, setAccountName] = useState("");
  const [error, setError] = useState(null);
  const [view, setView] = useState('home'); // 'home' or 'detail'
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [transcript, setTranscript] = useState({ loading: false, entries: [], milestones: [] });
  const [summary, setSummary] = useState(null);
  const [loadingCsv, setLoadingCsv] = useState(true);

  useEffect(() => {
    if (csvData) {
      processRawCSV(csvData);
    } else {
      setLoadingCsv(false);
    }
  }, [csvData]);

  const fetchTranscript = (conversation, conversationInfo) => {
    setTranscript({ loading: true, entries: [], milestones: [] });
    setSummary(null);
    console.log("Fetching transcript for conversation:", conversation, conversationInfo);
    const { selectedLocation } = business;
    const id = selectedLocation.studioId || auth.studio_id;
    const userId = `${id}-${conversation.number}`;
    const params = {
      clean: false,
      liveDashboard: false,
      userId: userId,
      businessId: id
    };

    superagent.get(`${window.Config.API_URL}/business/annotatedTranscripts`)
      .withCredentials()
      .query(params)
      .responseType('text')
      .then((resp) => {
        if (resp.text) {
          const entries = parseTranscript(resp.text);
          const milestones = getMilestones(entries, conversationInfo);
          
          if (entries.length > 0) {
            const startTime = entries[0].timestamp;
            const endTime = entries[entries.length - 1].timestamp;
            
            let assignedTo = "AI";
            const assignmentEvents = entries.filter(e => 
              e.label.toUpperCase() === 'BUSINESS_ACTION_CONVERSATION_ASSIGNMENT_STATUS' || 
              e.content.toUpperCase().includes('ASSIGNED TO')
            );
            
            if (assignmentEvents.length > 0) {
              const lastAssignment = assignmentEvents[assignmentEvents.length - 1].content;
              const match = lastAssignment.match(/assigned to (.*)/i);
              if (match && match[1]) {
                const name = match[1].trim();
                if (name.toLowerCase().includes('trainer')) {
                  assignedTo = "AI";
                } else {
                  assignedTo = name;
                }
              }
            }

            const outcomes = Array.from(new Set(
              milestones
                .filter(m => m.label.includes('DONE') || m.label.includes('CAPTURED') || m.label === 'ACTIONABLE_NOTIFICATION')
                .map(m => {
                  if (m.label === 'ACTIONABLE_NOTIFICATION') {
                    const intent = m.notificationIntent || 'ACTIONABLE';
                    return `NOTIFICATION (${intent})`;
                  }
                  return m.label.replace(/_/g, ' ');
                })
            ));

            setSummary({
              startTime,
              endTime,
              assignedTo,
              outcomes
            });
          }

          if (entries.length === 0) {
            setTranscript({ loading: false, entries: [], milestones: [], noData: true });
          } else {
            setTranscript({ loading: false, entries, milestones });
          }
        } else {
          throw new Error("Empty response from server");
        }
      })
      .catch((ex) => {
        console.error('Transcript fetch error:', ex);
        setTranscript({ loading: false, entries: [{ type: 'ERROR', content: 'Failed to load or parse transcript.' }], milestones: [] });
      });
  };

  /*
    The user has already pasted the following functions into the file:
    - parseTranscript(text)
    - getMilestones(entries, conversationInfo)
    - formatTimestamp(ts)
    - getNodeType(outcome)
    - various icon and style helpers (which may not work without tailwind)
    So, I will not repeat them here.
  */

  const processRawCSV = (text) => {
    const lines = text.split(/\r?\n/);
    // Extract account name from 3rd row
    if (lines.length >= 3) {
      const accountRow = lines[2].split(',');
      if (accountRow.length >= 2) {
        setAccountName(accountRow[1].replace(/"/g, '').trim());
      }
    }
    // Find where the actual data starts
    const headerIndex = lines.findIndex(line => line.includes("Conversation ID"));
    if (headerIndex !== -1) {
      const dataContent = lines.slice(headerIndex).join('\n');
      Papa.parse(dataContent, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            setError("Error parsing CSV data.");
            setLoadingCsv(false);
            return;
          }
          setData(results.data);
          setError(null);
          setLoadingCsv(false);
        },
      });
    } else {
      setError("Could not find 'Conversation ID' header in CSV data.");
      setLoadingCsv(false);
    }
  };

  const handleJourneyClick = (journey) => {
    setSelectedJourney(journey);
    setView('detail');
  };

  const handleConversationClick = (conversation) => {
    // Find the full conversation info from the original parsed CSV data
    const conversationInfo = data.find(row => row['Conversation ID'] === conversation.id);
    setSelectedConversation(conversation);
    fetchTranscript(conversation, conversationInfo);
  };

  const handleBackClick = () => {
    setView('home');
    setSelectedJourney(null);
    setSelectedConversation(null);
    setTranscript({ loading: false, entries: [], milestones: [] });
  };

  const journeys = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const grouped = data.reduce((acc, row) => {
      const id = row['Conversation ID'];
      if (id) {
        if (!acc[id]) acc[id] = [];
        acc[id].push(row);
      }
      return acc;
    }, {});

    const patterns = {};
    Object.keys(grouped).forEach(id => {
      const rows = grouped[id].sort((a, b) => (parseInt(a['Outcome #'], 10) || 0) - (parseInt(b['Outcome #'], 10) || 0));
      if (rows.length === 0) return;

      const firstRow = rows[0];
      const nodes = [{ label: firstRow['Conversation source'] || 'Unknown', type: 'source' }];
      
      rows.forEach(row => {
        if (row['Outcome']) {
          let label = row['Outcome'];
          if (String(label).toUpperCase() === 'NOTIFICATION' && row['Notification Intent']) {
            label = `NOTIFICATION (${row['Notification Intent']})`;
          }
          nodes.push({ label, type: getNodeType(row['Outcome']) });
        }
      });

      const rawString = nodes.map(n => n.label).join(' -> ');
      const convInfo = { id, number: firstRow['Conversation Number/ Webchat ID'] || 'Unknown', name: firstRow['Guest name'] || 'Guest' };

      if (patterns[rawString]) {
        patterns[rawString].frequency++;
        patterns[rawString].conversations.push(convInfo);
      } else {
        patterns[rawString] = { nodes, frequency: 1, rawString, conversations: [convInfo] };
      }
    });

    return Object.values(patterns).sort((a, b) => b.frequency - a.frequency);
  }, [data]);

  const chartData = useMemo(() => {
    return journeys.slice(0, 10).map(j => ({
      name: j.rawString.length > 40 ? j.rawString.substring(0, 37) + '...' : j.rawString,
      frequency: j.frequency
    }));
  }, [journeys]);

  if (error) {
    return <div style={{ padding: '20px', color: 'red' }}>Error: {error}</div>;
  }

  if (view === 'detail' && selectedJourney) {
    return (
      <div style={{ fontFamily: 'sans-serif', padding: '2rem', backgroundColor: '#f0f4f8', minHeight: '80vh' }}>
        <header style={{ marginBottom: '2rem' }}>
          <button onClick={handleBackClick} style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#0088cc', fontWeight: 'bold', marginBottom: '1rem' }}>
            <span className="material-icons">arrow_back</span> Back to Journeys
          </button>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#333' }}>Journey Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', padding: '1rem', backgroundColor: 'white', borderRadius: '8px', marginTop: '1rem', border: '1px solid #e0e0e0' }}>
            {selectedJourney.nodes.map((node, nIdx) => {
              const lowerLabel = node.label.toLowerCase();
              let bg = '#f9f9f9', border = '#ddd', text = '#333';
              
              if (lowerLabel.includes('cancel')) {
                bg = '#ffebee'; border = '#ffcdd2'; text = '#c62828';
              } else if (lowerLabel.includes('notification')) {
                bg = '#fff3e0'; border = '#ffe0b2'; text = '#e65100';
              } else if (lowerLabel.includes('booking') || lowerLabel.includes('booked') || lowerLabel.includes('done') || lowerLabel.includes('captured')) {
                bg = '#f1f8e9'; border = '#c5e1a5'; text = '#2e7d32';
              } else if (node.type === 'source') {
                bg = '#eaf6ff'; border = '#bce2ff'; text = '#0088cc';
              }

              return (
                <React.Fragment key={nIdx}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: '24px', border: `1px solid ${border}`, whiteSpace: 'nowrap', backgroundColor: bg, color: text }}>
                    <OutcomeIcon type={node.type} />
                    <span style={{ fontWeight: '800', fontSize: '0.85rem' }}>{node.label}</span>
                  </div>
                  {nIdx < selectedJourney.nodes.length - 1 && <span className="material-icons" style={{color: '#aaa'}}>arrow_forward</span>}
                </React.Fragment>
              );
            })}
          </div>
        </header>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2rem' }}>
          {/* Conversations List */}
          <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e0e0e0', padding: '1.5rem', maxHeight: '60vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>Conversations ({selectedJourney.conversations.length})</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {selectedJourney.conversations.map(conv => (
                <div
                  key={conv.id}
                  onClick={() => handleConversationClick(conv)}
                  style={{
                    padding: '1rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    border: '1px solid #ddd',
                    backgroundColor: selectedConversation?.id === conv.id ? '#eaf6ff' : 'transparent',
                    transition: 'background-color 0.2s'
                  }}
                >
                  <div style={{ fontWeight: 'bold', color: '#333' }}>{conv.name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#666' }}>ID: {conv.id}</div>
                  <div style={{ fontSize: '0.8rem', color: '#666' }}>Number: {conv.number}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Transcript View */}
          <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e0e0e0', padding: '1.5rem' }}>
            <div style={{ height: '55vh', overflowY: 'auto', paddingRight: '1rem' }}>
              {transcript.loading && <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}><Loading /></div>}
              {!transcript.loading && transcript.noData && <div style={{ color: '#666' }}>No transcript data available for this conversation.</div>}
              {!transcript.loading && transcript.entries.length === 0 && !transcript.noData && <div style={{ color: '#666' }}>Select a conversation to view the conversation summary</div>}
              
              {summary && (
                <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid #e0e0e0', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
                  
                  {/* Top Header Row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{ width: '48px', height: '48px', backgroundColor: '#e6e8ff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5c6bc0' }}>
                        <span className="material-icons" style={{ fontSize: '28px' }}>ssid_chart</span>
                      </div>
                      <h3 style={{ margin: 0, fontSize: '1.4rem', fontWeight: '800', color: '#0a192f' }}>Conversation Summary</h3>
                    </div>
                    {summary.assignedTo && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#fcf4ff', border: '1px solid #e1bee7', color: '#8e24aa', padding: '0.5rem 1rem', borderRadius: '24px', fontWeight: 'bold', fontSize: '0.85rem', letterSpacing: '0.5px' }}>
                        <span className="material-icons" style={{ fontSize: '18px' }}>account_circle</span>
                        <span>ASSIGNED TO: {summary.assignedTo.toUpperCase()}</span>
                      </div>
                    )}
                  </div>

                  {/* Two Column Layout for Timeline and Outcomes */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) minmax(300px, 1fr)', gap: '2rem' }}>
                    
                    {/* Timeline Column */}
                    <div>
                      <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#90a4ae', letterSpacing: '1px', marginBottom: '1rem', textTransform: 'uppercase' }}>
                        Timeline
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{ flex: 1, padding: '1rem', borderRadius: '12px', border: '1px solid #e0e0e0', backgroundColor: '#ffffff' }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#90a4ae', marginBottom: '0.5rem' }}>STARTED</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span className="material-icons" style={{ color: '#64b5f6', fontSize: '18px' }}>schedule</span>
                            <div style={{ fontWeight: '800', color: '#263238', fontSize: '1rem' }}>
                              <div>{moment(summary.startTime).format('YYYY-MM-DD')}</div>
                              <div>{moment(summary.startTime).format('HH:mm:ss')}</div>
                            </div>
                          </div>
                        </div>
                        <span className="material-icons" style={{ color: '#b0bec5' }}>arrow_forward</span>
                        <div style={{ flex: 1, padding: '1rem', borderRadius: '12px', border: '1px solid #e0e0e0', backgroundColor: '#ffffff' }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#90a4ae', marginBottom: '0.5rem' }}>ENDED</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span className="material-icons" style={{ color: '#90a4ae', fontSize: '18px' }}>schedule</span>
                            <div style={{ fontWeight: '800', color: '#263238', fontSize: '1rem' }}>
                              <div>{moment(summary.endTime).format('YYYY-MM-DD')}</div>
                              <div>{moment(summary.endTime).format('HH:mm:ss')}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Outcomes Column */}
                    <div>
                      <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#90a4ae', letterSpacing: '1px', marginBottom: '1rem', textTransform: 'uppercase' }}>
                        Outcome Graph Summary
                      </div>
                      {summary.outcomes.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {summary.outcomes.map((outcome, i) => {
                            const lowerOutcome = outcome.toLowerCase();
                            let bg = '#f1f8e9', border = '#c5e1a5', text = '#2e7d32', icon = 'check_circle_outline';
                            
                            if (lowerOutcome.includes('cancel')) {
                              bg = '#ffebee'; border = '#ffcdd2'; text = '#c62828'; icon = 'cancel';
                            } else if (lowerOutcome.includes('notification')) {
                              bg = '#fff3e0'; border = '#ffe0b2'; text = '#e65100'; icon = 'notifications_active';
                            }

                            return (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: bg, border: `1px solid ${border}`, color: text, padding: '0.75rem 1rem', borderRadius: '24px', fontWeight: '800', fontSize: '0.85rem', alignSelf: 'flex-start' }}>
                                <span className="material-icons" style={{ fontSize: '18px' }}>{icon}</span>
                                <span>{outcome.toUpperCase()}</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div style={{ color: '#90a4ae', fontSize: '0.85rem', fontStyle: 'italic' }}>No outcome recorded.</div>
                      )}
                    </div>

                  </div>

                </div>
              )}

              {/* Milestones View */}
              {!transcript.loading && transcript.milestones.length > 0 && (
                <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid #e0e0e0', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
                    <div style={{ width: '48px', height: '48px', backgroundColor: '#f0f4f8', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0088cc' }}>
                      <span className="material-icons" style={{ fontSize: '28px' }}>linear_scale</span>
                    </div>
                    <h3 style={{ margin: 0, fontSize: '1.4rem', fontWeight: '800', color: '#0a192f' }}>Key Milestones</h3>
                  </div>
                  
                  <div style={{ position: 'relative', paddingLeft: '25px' }}>
                    <div style={{ position: 'absolute', left: '42px', top: '24px', bottom: '24px', width: '2px', backgroundColor: '#e0e0e0' }}></div>
                    {transcript.milestones.map((milestone, index) => {
                      const lowerLabel = milestone.label.toLowerCase();
                      let bg = '#eaf6ff', border = '#bce2ff', text = '#0088cc';
                      
                      if (lowerLabel.includes('cancel')) {
                        bg = '#ffebee'; border = '#ffcdd2'; text = '#c62828';
                      } else if (lowerLabel.includes('notification')) {
                        bg = '#fff3e0'; border = '#ffe0b2'; text = '#e65100';
                      } else if (lowerLabel.includes('booking') || lowerLabel.includes('done') || lowerLabel.includes('captured')) {
                        bg = '#f1f8e9'; border = '#c5e1a5'; text = '#2e7d32';
                      }

                      return (
                        <div key={index} style={{ display: 'flex', gap: '1.25rem', marginBottom: '1.5rem', position: 'relative' }}>
                          <div style={{ zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '50%', border: `2px solid ${text}`, backgroundColor: '#ffffff', color: text, marginTop: '2px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                            <MilestoneIcon label={milestone.label} />
                          </div>
                          <div style={{ flex: 1, padding: '1rem', borderRadius: '12px', border: '1px solid #e0e0e0', backgroundColor: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.75rem' }}>
                              <span style={{ backgroundColor: bg, border: `1px solid ${border}`, color: text, padding: '0.25rem 0.75rem', borderRadius: '16px', fontWeight: '800', fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                                {milestone.label.replace(/_/g, ' ')}
                              </span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', color: '#90a4ae', fontWeight: '700' }}>
                                <span className="material-icons" style={{ fontSize: '15px' }}>schedule</span>
                                {formatTimestamp(milestone.timestamp)}
                              </div>
                            </div>
                            <p style={{ fontSize: '0.95rem', color: '#263238', margin: 0, lineHeight: '1.5' }}>{milestone.content}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'sans-serif', padding: '2rem', backgroundColor: '#f0f4f8' }}>
      <header style={{ marginBottom: '3rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
          <div style={{ padding: '0.5rem', backgroundColor: '#0088cc', borderRadius: '6px', color: 'white' }}><span className="material-icons">timeline</span></div>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#333' }}>Customer Journey Analytics</h1>
        </div>
        {accountName && <p style={{ color: '#666' }}>Account: {accountName}</p>}
      </header>

      {data.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
          {/* Chart Section */}
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#333' }}>
              <span className="material-icons">bar_chart</span> Frequency Distribution (Top 10)
            </h3>
            <div style={{ height: '350px', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ top: 20, right: 30, left: 230, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    width={200} 
                    tick={<CustomYAxisTick />}
                    interval={0}
                  >
                    <Label value="Journey Patterns" angle={-90} position="insideLeft" dx={-220} style={{ textAnchor: 'middle', fill: '#666' }} />
                  </YAxis>
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc' }}
                    cursor={{fill: 'rgba(206, 206, 206, 0.2)'}}
                    formatter={(value) => [value, 'Frequency']}
                  />
                  <Legend />
                  <Bar dataKey="frequency" fill={COLORS[0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Journeys List */}
          <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', color: '#333' }}>All Journey Patterns</h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <TransitionGroup>
                {journeys.map((journey) => (
                  <CSSTransition key={journey.rawString} timeout={500} classNames="item">
                    <div onClick={() => handleJourneyClick(journey)} style={{ backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', border: '1px solid #e0e0e0', cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                          {journey.nodes.map((node, nIdx) => {
                            const lowerLabel = node.label.toLowerCase();
                            let bg = '#f9f9f9', border = '#ddd', text = '#333';
                            
                            if (lowerLabel.includes('cancel')) {
                              bg = '#ffebee'; border = '#ffcdd2'; text = '#c62828';
                            } else if (lowerLabel.includes('notification')) {
                              bg = '#fff3e0'; border = '#ffe0b2'; text = '#e65100';
                            } else if (lowerLabel.includes('booking') || lowerLabel.includes('booked') || lowerLabel.includes('done') || lowerLabel.includes('captured')) {
                              bg = '#f1f8e9'; border = '#c5e1a5'; text = '#2e7d32';
                            } else if (node.type === 'source') {
                              bg = '#eaf6ff'; border = '#bce2ff'; text = '#0088cc';
                            }

                            return (
                              <React.Fragment key={nIdx}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: '24px', border: `1px solid ${border}`, whiteSpace: 'nowrap', backgroundColor: bg, color: text }}>
                                  <OutcomeIcon type={node.type} />
                                  <span style={{ fontWeight: '800', fontSize: '0.85rem' }}>{node.label}</span>
                                </div>
                                {nIdx < journey.nodes.length - 1 && <span className="material-icons" style={{color: '#aaa'}}>arrow_forward</span>}
                              </React.Fragment>
                            );
                          })}
                        </div>
                        <div style={{ textAlign: 'right', paddingLeft: '1.5rem', borderLeft: '1px solid #e0e0e0', marginLeft: '1.5rem' }}>
                          <div style={{ fontSize: '1.5rem', fontWeight: '900', color: '#333' }}>{journey.frequency}</div>
                          <div style={{ fontSize: '0.625rem', textTransform: 'uppercase', color: '#888' }}>Count</div>
                        </div>
                      </div>
                    </div>
                  </CSSTransition>
                ))}
              </TransitionGroup>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(80vh - 200px)' }}>
          {loadingCsv ? <Loading /> : <div>No journey data available for the selected period.</div>}
        </div>
      )}
      <ReactTooltip id="journey-tooltip" place="top" effect="solid" multiline={true} />
    </div>
  );
};

NewJourneyView.propTypes = {
  csvData: PropTypes.string,
  business: PropTypes.object,
  auth: PropTypes.object,
};

export default NewJourneyView;
